import {
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import moment from 'moment'
import React from 'react'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import Tooltip from 'src/components/Tooltip/Tooltip'
import { ExtendCurrency } from 'src/enums'
import Crncy from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import { OrganizationReceiptRulesResponse } from 'src/models/organizations.models'
import { toDollars } from 'src/utils/currency.utils'
import { css } from 'styled-components'

interface OrgSettingsReceiptRulesBoxContentProps {
  receiptRule?: OrganizationReceiptRulesResponse
}

const OrgSettingsReceiptRulesBoxContent: React.FC<
  OrgSettingsReceiptRulesBoxContentProps
> = (props): React.ReactElement => {
  const translate = useTranslate()

  const { receiptRule } = props

  const isReceiptRulesOn = receiptRule?.enabled

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <span
        css={css`
          ${getFontCss(
            12,
            styleConstants.colors.gray_darkest,
            styleConstants.fontFamilies.regular,
          )};
          margin-bottom: 40px;
        `}
      >
        {translate(
          'An Organization Owner or Admin can change rules for the organization at any time. Cards will only be affected once Receipt Rules have been set. Visit our ${resourceCenter} to learn more.',
          {
            resourceCenter: (
              <ResourceCenterLink
                size={HoverableLinkSizes.Small}
                underline
                regular
              />
            ),
          },
        )}
      </span>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          margin-bottom: 28px;
        `}
      >
        <span
          css={css`
            ${getFontCss(
              14,
              styleConstants.colors.gray_darkest,
              styleConstants.fontFamilies.regular,
            )};
            margin-bottom: 8px;
          `}
        >
          {translate('Receipt Rules')}
        </span>
        <span
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          {translate(isReceiptRulesOn ? 'ON' : 'OFF')}
        </span>
      </div>
      {isReceiptRulesOn && (
        <>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              margin-bottom: 28px;
            `}
          >
            <span
              css={css`
                ${getFontCss(
                  14,
                  styleConstants.colors.gray_darkest,
                  styleConstants.fontFamilies.regular,
                )};
                margin-bottom: 8px;
                display: flex;
              `}
            >
              {translate('Start Date')}
              <Tooltip
                openLeft={false}
                css={css`
                  height: 14px;
                  margin-bottom: 6px;
                `}
              >
                {translate(
                  'The receipt rules will not be applied to transactions authorized before your start date',
                )}
              </Tooltip>
            </span>
            <span
              css={css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              {moment(receiptRule?.effectiveAt).format('LL')}
            </span>
          </div>
          {(receiptRule?.usdThresholdCents !== undefined ||
            receiptRule?.cadThresholdCents !== undefined) && (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                margin-bottom: 28px;
              `}
            >
              <span
                css={css`
                  ${getFontCss(
                    14,
                    styleConstants.colors.gray_darkest,
                    styleConstants.fontFamilies.regular,
                  )};
                  margin-bottom: 8px;
                `}
              >
                {translate('Minimum Transaction Amount')}
              </span>
              {receiptRule?.usdThresholdCents !== undefined && (
                <span
                  css={css`
                    margin-bottom: 8px;
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.regular,
                    )};
                  `}
                >
                  {translate('${threshold} ${faintText}', {
                    threshold: (
                      <Crncy
                        currency={ExtendCurrency.Usd}
                        amount={toDollars(receiptRule.usdThresholdCents)}
                        hideCurrency
                      />
                    ),
                    faintText: (
                      <span
                        css={css`
                          ${getFontCss(
                            16,
                            styleConstants.colors.gray_darkest,
                            styleConstants.fontFamilies.regular,
                          )};
                        `}
                      >
                        {ExtendCurrency.Usd}
                      </span>
                    ),
                  })}
                </span>
              )}
              {receiptRule?.cadThresholdCents !== undefined && (
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.regular,
                    )};
                  `}
                >
                  {translate('${threshold} ${faintText}', {
                    threshold: (
                      <Crncy
                        currency={ExtendCurrency.Cad}
                        amount={toDollars(receiptRule.cadThresholdCents)}
                        hideCurrency
                      />
                    ),
                    faintText: (
                      <span
                        css={css`
                          ${getFontCss(
                            16,
                            styleConstants.colors.gray_darkest,
                            styleConstants.fontFamilies.regular,
                          )};
                        `}
                      >
                        {ExtendCurrency.Cad}
                      </span>
                    ),
                  })}
                </span>
              )}
            </div>
          )}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              row-gap: 8px;
            `}
          >
            <span
              css={css`
                ${getFontCss(
                  14,
                  styleConstants.colors.gray_darkest,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              {translate('Card Types Affected')}
            </span>
            {receiptRule?.standardCardsEnabled && (
              <span
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                {translate('Standard Virtual Cards')}
              </span>
            )}
            {receiptRule?.recurringCardsEnabled && (
              <span
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                {translate('Recurring Virtual Cards')}
              </span>
            )}
            {receiptRule?.physicalCardsEnabled && (
              <span
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                {translate('Physical Cards')}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default OrgSettingsReceiptRulesBoxContent
